import { WaypointActionType, WaypointActionStatusName, SupplyType, SamplesDeliveryType, StorageRef } from '@caresend/types';
import { hasStatus, isMailInAction, capitalize, objectFilter, isLocationObjectValid, initShipment, initLocationObject, arrayToObj, initElementTimestamp } from '@caresend/utils';
import update from 'immutability-helper';
import { defineComponent, ref, computed, watch } from 'vue';
import __vue_component__$1 from '../../../../../buttons/ButtonComponent/ButtonComponent.vue.js';
import __vue_component__ from '../../../../../cards/BasicCard/BasicCard.vue.js';
import __vue_component__$2 from '../../../../../inputs/FilePicker/FilePicker.vue.js';
import __vue_component__$3 from '../../../../../inputs/FilePickerViewer.vue.js';
import __vue_component__$4 from '../../../../../inputs/Radio/Radio.vue.js';
import __vue_component__$5 from '../../../../../inputs/RichSelect/RichSelect.vue.js';
import __vue_component__$6 from '../../../../../inputs/SelectComponent.vue.js';
import __vue_component__$7 from '../../../../../inputs/TextInput/TextInput.vue.js';
import { getPatientWaypointAddressOptions, getMailInBoxPickupWaypoint, NONE } from './helpers.js';
import { usePortalStore } from '../../../store/portal/index.js';
import 'firebase/auth';
import '../../../../../../database/firebaseConfig/cloudFunctions.js';
import '../../../../../../database/firebaseConfig/init.js';
import '../../../../../../database/firebaseConfig/methods.js';
import '@sentry/vue';
import { toastError } from '../../../../../../functions/toast/toast.js';
import '@fortawesome/fontawesome-svg-core';
import 'buefy';
import '../../../../../ui/Icon.vue.js';
import '../../../../../../functions/configuration/icons.js';
import '../../../../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../../../../functions/tracking/index.js';
import '../../../../../../router/helpers/initRouter.js';
import { getStore } from '../../../../../../store/helpers/initStore.js';
import '@firebase/auth';
import { shopifyURL } from '../../../../../../database/firebaseConfig/credentials.js';
import 'browser-image-compression';
import 'firebase/storage';
import 'js-sha256';

var script = defineComponent({
    name: 'ManualWaypointAction',
    components: {
        BasicCard: __vue_component__,
        ButtonComponent: __vue_component__$1,
        FilePicker: __vue_component__$2,
        FilePickerViewer: __vue_component__$3,
        Radio: __vue_component__$4,
        RichSelect: __vue_component__$5,
        SelectComponent: __vue_component__$6,
        TextInput: __vue_component__$7,
    },
    props: {
        indexInArray: {
            type: Number,
            default: 0,
        },
        waypointAction: {
            type: Object,
            required: true,
        },
        waypointID: {
            type: String,
            required: true,
        },
        localWpaContext: {
            type: Object,
            required: true,
        },
    },
    setup(props, { emit }) {
        var _a, _b;
        const store = getStore();
        const portalStore = usePortalStore();
        const isCreatingShopifyOrder = ref(false);
        const waypoint = computed(() => portalStore.waypoints.get(props.waypointID));
        const isWaypointActionMailIn = computed(() => props.waypointAction.type === WaypointActionType.MAIL_IN);
        const inputDisabled = computed(() => hasStatus(props.waypointAction, [
            WaypointActionStatusName.COMPLETE,
            WaypointActionStatusName.CANCELED,
        ]));
        const patientWaypointOptions = computed(() => { var _a; return getPatientWaypointAddressOptions((_a = waypoint.value) === null || _a === void 0 ? void 0 : _a.itineraryID); });
        const selectedMailInBoxPickupWaypoint = computed(() => { var _a, _b, _c; return (_c = (_b = getMailInBoxPickupWaypoint((_a = waypoint.value) === null || _a === void 0 ? void 0 : _a.itineraryID, props.waypointID)) === null || _b === void 0 ? void 0 : _b.id) !== null && _c !== void 0 ? _c : NONE; });
        const mailInBoxSupplyID = computed(() => {
            var _a;
            return isMailInAction(props.waypointAction)
                ? (_a = props.waypointAction.mailInBoxSupplyID) !== null && _a !== void 0 ? _a : ''
                : '';
        });
        const getSupplyName = (supplyID) => {
            var _a;
            const supply = store.getters['variables/getSupplyByID'](supplyID);
            return (_a = supply === null || supply === void 0 ? void 0 : supply.name) !== null && _a !== void 0 ? _a : 'Unknown supply';
        };
        const supplyOptions = computed(() => {
            var _a, _b;
            return Object.values((_b = (_a = store.state.variables.variables) === null || _a === void 0 ? void 0 : _a.supplies) !== null && _b !== void 0 ? _b : {})
                .filter((supply) => supply.type === SupplyType.LAB_SHIPMENT_BOX)
                /** supply names are 'small, medium, large' */
                .sort((a, b) => b.name.localeCompare(a.name))
                .map((supply) => ({
                id: supply.id,
                label: getSupplyName(supply.id),
            }));
        });
        const placeGroup = computed(() => {
            var _a, _b, _c, _d;
            if (!((_a = waypoint.value) === null || _a === void 0 ? void 0 : _a.placeID))
                return;
            const placeGroupID = (_c = (_b = store.state.places.places) === null || _b === void 0 ? void 0 : _b[waypoint.value.placeID]) === null || _c === void 0 ? void 0 : _c.placeGroupID;
            if (!placeGroupID)
                return;
            return (_d = store.state.places.placeGroups) === null || _d === void 0 ? void 0 : _d[placeGroupID];
        });
        const defaultSupportedTypes = Object.values(WaypointActionType)
            .filter((wpaType) => wpaType !== WaypointActionType.PATIENT_ACTION);
        const supportedTypes = ref((_b = (_a = placeGroup.value) === null || _a === void 0 ? void 0 : _a.supportedActionTypes) !== null && _b !== void 0 ? _b : defaultSupportedTypes);
        const supportedTypeRadioOptions = computed(() => {
            var _a, _b;
            return ((_b = (_a = placeGroup.value) === null || _a === void 0 ? void 0 : _a.supportedActionTypes) !== null && _b !== void 0 ? _b : defaultSupportedTypes)
                .map((type) => ({
                id: type,
                label: capitalize(type),
            }));
        });
        const instructionPlaceholder = computed(() => {
            const formattedAction = props.waypointAction.type === WaypointActionType.PICKUP
                ? 'picked up'
                : 'dropped off';
            return `I ${formattedAction} the samples`;
        });
        const getBookingOptionText = (procedureID) => {
            var _a, _b, _c;
            if (!procedureID)
                return 'No procedure';
            const procedure = store.state.procedures.procedures[procedureID];
            const productInfo = store.getters['procedures/getProcedureProductInfo'](procedure === null || procedure === void 0 ? void 0 : procedure.id);
            const patientID = store.getters['procedures/getProcedurePatientID'](procedureID);
            if (!patientID)
                return `${(_a = productInfo === null || productInfo === void 0 ? void 0 : productInfo.friendlyName) !== null && _a !== void 0 ? _a : productInfo === null || productInfo === void 0 ? void 0 : productInfo.name}`;
            const patientName = store.getters['users/getUserFullName'](patientID);
            let labLabel = '';
            Object.keys((_b = procedure === null || procedure === void 0 ? void 0 : procedure.dropOffPlaceGroups) !== null && _b !== void 0 ? _b : {}).forEach((dropOffPlaceGroupID) => {
                var _a, _b, _c, _d, _e;
                if ((_b = (_a = store.state.places.placeGroups) === null || _a === void 0 ? void 0 : _a[dropOffPlaceGroupID]) === null || _b === void 0 ? void 0 : _b.name) {
                    labLabel += `${(_e = (_d = (_c = store.state.places.placeGroups) === null || _c === void 0 ? void 0 : _c[dropOffPlaceGroupID]) === null || _d === void 0 ? void 0 : _d.name) !== null && _e !== void 0 ? _e : ''} `;
                }
            });
            return `${patientName} (${(_c = productInfo === null || productInfo === void 0 ? void 0 : productInfo.friendlyName) !== null && _c !== void 0 ? _c : productInfo === null || productInfo === void 0 ? void 0 : productInfo.name}) ${labLabel}`;
        };
        const proceduresWithMultipleDropOffs = computed(() => {
            var _a;
            return Object.keys((_a = props.waypointAction.procedures) !== null && _a !== void 0 ? _a : {}).filter((procedureID) => {
                const allWaypointActions = {
                    ...portalStore.itinerary.getters.currentWaypointActions(),
                    ...props.localWpaContext,
                };
                const waypointActionsWithProcedureReference = objectFilter(allWaypointActions, (wpa) => {
                    var _a;
                    return !!wpa && (wpa.type === WaypointActionType.DROPOFF
                        || wpa.type === WaypointActionType.MAIL_IN)
                        && !!((_a = wpa.procedures) === null || _a === void 0 ? void 0 : _a[procedureID]);
                });
                return Object.keys(waypointActionsWithProcedureReference).length > 1;
            });
        });
        const orphanedProcedureIDs = computed(() => {
            var _a;
            return Object.keys((_a = props.waypointAction.procedures) !== null && _a !== void 0 ? _a : {}).filter((procedureID) => portalStore.itinerary.getters.procedureIsDropOffOrphaned(procedureID));
        });
        const shouldDisplayFailures = computed(() => orphanedProcedureIDs.value.length > 0
            || proceduresWithMultipleDropOffs.value.length > 0);
        const getProcedureOption = (procedureID) => {
            let color;
            if (proceduresWithMultipleDropOffs.value.includes(procedureID)) {
                color = 'red';
            }
            else if (orphanedProcedureIDs.value.includes(procedureID)) {
                color = 'orange';
            }
            return {
                id: procedureID,
                label: getBookingOptionText(procedureID),
                color: color,
            };
        };
        const selectedProcedures = computed(() => {
            var _a;
            const procedureIDs = Object.keys((_a = props.waypointAction.procedures) !== null && _a !== void 0 ? _a : {});
            return procedureIDs.map(getProcedureOption);
        });
        const allOrClearOption = computed(() => {
            if (selectedProcedures.value.length === 0) {
                return {
                    id: 'all',
                    label: 'Select all procedures',
                };
            }
            return {
                id: 'clear',
                label: 'Clear selection',
            };
        });
        const proceduresOptions = computed(() => {
            var _a;
            const procedureIDs = Object.keys((_a = portalStore.itinerary.getters.procedures()) !== null && _a !== void 0 ? _a : {});
            const filteredProcedureIDs = procedureIDs.filter((procedureID) => {
                var _a, _b, _c;
                const procedure = store.state.procedures.procedures[procedureID];
                if (!procedure)
                    throw Error(`Missing procedure ${procedureID}`);
                // Once a place group is set on the waypoint action, only allow
                // selection of procedures matching that place group unless mail-in
                if (isWaypointActionMailIn.value
                    && ((_a = procedure.supplyInstructionConfig) === null || _a === void 0 ? void 0 : _a.samplesDeliveryType) === SamplesDeliveryType.MAIL_IN)
                    return true;
                const placeGroupID = (_b = placeGroup.value) === null || _b === void 0 ? void 0 : _b.id;
                if (!placeGroupID)
                    return true;
                return !!((_c = procedure.dropOffPlaceGroups) === null || _c === void 0 ? void 0 : _c[placeGroupID]);
            });
            return [
                allOrClearOption.value,
                ...filteredProcedureIDs.map(getProcedureOption),
            ];
        });
        const getMailInActionShipment = (action) => {
            var _a, _b;
            const shipmentID = Object.keys((_a = action.shipments) !== null && _a !== void 0 ? _a : {})[0];
            if (!shipmentID)
                return;
            return (_b = portalStore.draftShipments.get(shipmentID)) !== null && _b !== void 0 ? _b : store.state.supplies.shipments[shipmentID];
        };
        const shipment = computed(() => getMailInActionShipment(props.waypointAction));
        const shipmentPicture = computed(() => {
            if (!shipment.value)
                return undefined;
            return shipment.value.shipmentBoxImage;
        });
        const updateShipment = (updateSpec) => {
            if (!shipment.value) {
                toastError('No shipment found');
                return;
            }
            const newShipment = update(shipment.value, updateSpec);
            portalStore.draftShipments.set(newShipment);
        };
        const getProcedureSelectionShipmentUpdateSpec = (
        /** Place group of destination lab (not mail-in drop off location) */
        placeGroupID) => {
            var _a;
            if (!placeGroupID) {
                return {
                    shippingRecipient: { $set: '' },
                    shippingAddress: { $set: initLocationObject() },
                };
            }
            const firstProcedurePlaceGroup = (_a = store.state.places.placeGroups) === null || _a === void 0 ? void 0 : _a[placeGroupID];
            const places = store.getters['places/getPlacesOnPlaceGroup'](placeGroupID);
            const firstMailInEligiblePlace = places === null || places === void 0 ? void 0 : places.find((place) => place.allowsMailIn);
            const shippingRecipient = firstProcedurePlaceGroup === null || firstProcedurePlaceGroup === void 0 ? void 0 : firstProcedurePlaceGroup.name;
            const shippingAddress = firstMailInEligiblePlace === null || firstMailInEligiblePlace === void 0 ? void 0 : firstMailInEligiblePlace.location;
            return {
                shippingRecipient: { $set: shippingRecipient !== null && shippingRecipient !== void 0 ? shippingRecipient : '' },
                shippingAddress: { $set: shippingAddress !== null && shippingAddress !== void 0 ? shippingAddress : initLocationObject() },
            };
        };
        const getProcedureSelectionUpdatedWaypointAction = (procedureIDs, 
        /** Place group of destination lab */
        placeGroupID) => {
            const placeGroupIdUpdateSpec = placeGroupID
                ? { placeGroupID: { $set: placeGroupID } }
                : { $unset: ['placeGroupID'] };
            const updatedWpa = update(props.waypointAction, {
                procedures: {
                    $set: arrayToObj(procedureIDs.map((id) => initElementTimestamp(id)), 'id'),
                },
                ...placeGroupIdUpdateSpec,
            });
            return updatedWpa;
        };
        /**
         * Upon procedure selection,
         * - Update the waypoint action's procedures
         * - Update the waypoint action's place group ID based on the first
         *   procedure's place group
         * - For a mail-in waypoint action, update the shipping recipient and
         *   address on the shipment
         */
        const handleProceduresSelection = (procedures) => {
            var _a;
            let procedureIDs = [];
            if (procedures.find((procedure) => procedure.id === 'all')) {
                procedureIDs = proceduresOptions.value
                    .map((procedure) => procedure.id)
                    .filter((procedureID) => procedureID !== 'all' && procedureID !== 'clear');
            }
            else if (procedures.find((procedure) => procedure.id === 'clear')) {
                procedureIDs = [];
            }
            else {
                procedureIDs = procedures.map((procedure) => procedure.id);
            }
            const firstProcedureID = procedureIDs[0];
            let firstProcedure;
            if (firstProcedureID) {
                firstProcedure = store.state.procedures.procedures[firstProcedureID];
                if (!firstProcedure)
                    throw Error(`Missing procedure ${firstProcedureID}`);
            }
            const firstProcedurePlaceGroupID = Object.keys((_a = firstProcedure === null || firstProcedure === void 0 ? void 0 : firstProcedure.dropOffPlaceGroups) !== null && _a !== void 0 ? _a : {})[0];
            if (isWaypointActionMailIn.value) {
                const shipmentUpdateSpec = getProcedureSelectionShipmentUpdateSpec(firstProcedurePlaceGroupID);
                updateShipment(shipmentUpdateSpec);
            }
            const updatedWpa = getProcedureSelectionUpdatedWaypointAction(procedureIDs, firstProcedurePlaceGroupID);
            emit('update', {
                wpa: updatedWpa,
                commitShipments: true,
            });
        };
        const setInstructions = (instructions) => {
            const newWpa = update(props.waypointAction, {
                instructions: { $set: instructions },
            });
            emit('update', {
                wpa: newWpa,
            });
        };
        const setType = (type) => {
            const newWpa = update(props.waypointAction, {
                type: { $set: type },
            });
            emit('update', {
                wpa: newWpa,
            });
        };
        const initDraftItineraryMailInActionShipment = () => {
            const newShipment = initShipment();
            newShipment.waypointActionID = props.waypointAction.id;
            portalStore.draftShipments.set(newShipment);
            const newWpa = update(props.waypointAction, {
                shipments: {
                    $set: {
                        [newShipment.id]: true,
                    },
                },
            });
            emit('update', {
                wpa: newWpa,
                commitShipments: true,
            });
        };
        watch(() => isWaypointActionMailIn.value, () => {
            if (isWaypointActionMailIn.value && !shipment.value) {
                initDraftItineraryMailInActionShipment();
            }
        }, { immediate: true });
        const createShopifyOrder = async () => {
            var _a, _b, _c;
            if (!shipment.value) {
                toastError('No shipment found');
                return;
            }
            isCreatingShopifyOrder.value = true;
            const draftWaypoints = (_a = portalStore.waypoints.get(props.waypointID)) === null || _a === void 0 ? void 0 : _a.computed.draftWaypoints;
            const draftWaypoint = Object.values(draftWaypoints !== null && draftWaypoints !== void 0 ? draftWaypoints : {})[0];
            /** TODO: fix type */
            const shopifyInfo = await ((_b = store.dispatch) === null || _b === void 0 ? void 0 : _b.call(store, 'supplies/createShopifyOrder', {
                supplyShipment: shipment.value,
                primaryContact: draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.contact,
                procedureID: Object.keys((_c = draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.procedures) !== null && _c !== void 0 ? _c : {})[0],
            }));
            if (shopifyInfo) {
                updateShipment({
                    shopifyInfo: { $set: shopifyInfo },
                });
            }
            isCreatingShopifyOrder.value = false;
        };
        const shopifyOrderIdValue = computed(() => {
            var _a, _b, _c, _d, _e;
            // Value initialized as 0, return undefined if falsy
            if (!((_b = (_a = shipment.value) === null || _a === void 0 ? void 0 : _a.shopifyInfo) === null || _b === void 0 ? void 0 : _b.shopifyOrderID))
                return undefined;
            return (_e = (_d = (_c = shipment.value) === null || _c === void 0 ? void 0 : _c.shopifyInfo) === null || _d === void 0 ? void 0 : _d.shopifyOrderID) === null || _e === void 0 ? void 0 : _e.toString();
        });
        const shopifyButtonLabel = computed(() => {
            var _a, _b;
            if (!((_b = (_a = shipment.value) === null || _a === void 0 ? void 0 : _a.shopifyInfo) === null || _b === void 0 ? void 0 : _b.shopifyOrderID))
                return 'Create Shopify order';
            return 'Open Shopify order';
        });
        const shopifyButtonEnabled = computed(() => {
            var _a, _b;
            return (!!((_a = shipment.value) === null || _a === void 0 ? void 0 : _a.shippingRecipient)
                && isLocationObjectValid((_b = shipment.value) === null || _b === void 0 ? void 0 : _b.shippingAddress));
        });
        const shopifyButtonHandler = async () => {
            var _a, _b, _c, _d;
            if (!((_b = (_a = shipment.value) === null || _a === void 0 ? void 0 : _a.shopifyInfo) === null || _b === void 0 ? void 0 : _b.shopifyOrderID)) {
                await createShopifyOrder();
                return;
            }
            const shopifyOrderURL = `${shopifyURL}/orders/${(_d = (_c = shipment.value) === null || _c === void 0 ? void 0 : _c.shopifyInfo) === null || _d === void 0 ? void 0 : _d.shopifyOrderID}`;
            window.open(shopifyOrderURL);
        };
        const setShippingTrackingURL = (trackingURL) => {
            updateShipment({
                trackingURL: {
                    $set: trackingURL,
                },
            });
        };
        const setShippingBarcodeValue = (labelBarcode) => {
            updateShipment({
                shippingLabelBarcode: { $set: labelBarcode },
            });
        };
        const storagePath = StorageRef.SHIPPING_LABELS;
        const setShippingLabelURL = (url) => {
            updateShipment({
                shippingLabelURL: { $set: url },
            });
        };
        const selectMailInSupplyID = (supplyID) => {
            const updatedWpa = update(props.waypointAction, {
                mailInBoxSupplyID: { $set: supplyID },
            });
            emit('update', {
                wpa: updatedWpa,
            });
        };
        const handleMailInBoxPickupWaypointSelection = (patientWaypointID) => {
            try {
                const mailInWaypointActionID = patientWaypointID !== NONE
                    ? props.waypointAction.id
                    : undefined;
                const waypointID = mailInWaypointActionID ? patientWaypointID : selectedMailInBoxPickupWaypoint.value;
                if (!waypointID)
                    throw Error('Missing waypointID');
                emit('setMailInWaypointActionID', {
                    waypointID,
                    mailInWaypointActionID,
                });
            }
            catch (error) {
                toastError(error);
            }
        };
        return {
            getBookingOptionText,
            getSupplyName,
            handleMailInBoxPickupWaypointSelection,
            handleProceduresSelection,
            inputDisabled,
            instructionPlaceholder,
            isCreatingShopifyOrder,
            isWaypointActionMailIn,
            mailInBoxSupplyID,
            orphanedProcedureIDs,
            patientWaypointOptions,
            proceduresOptions,
            proceduresWithMultipleDropOffs,
            selectMailInSupplyID,
            selectedMailInBoxPickupWaypoint,
            selectedProcedures,
            setInstructions,
            setShippingBarcodeValue,
            setShippingLabelURL,
            setShippingTrackingURL,
            setType,
            shipment,
            shipmentPicture,
            shopifyButtonEnabled,
            shopifyButtonHandler,
            shopifyButtonLabel,
            shopifyOrderIdValue,
            shouldDisplayFailures,
            storagePath,
            supplyOptions,
            supportedTypeRadioOptions,
            supportedTypes,
            waypoint,
        };
    },
});

export { script as default };
